<template>
  <li
    :data-testid="alert.id"
    :class="[
      'card relative mr-0 flex bg-white p-4 dark:border-navy-600 dark:bg-navy-900',
      {
        'text-gray-600': alert.muted,
      },
    ]"
  >
    <div class="col-icon mr-4 flex h-full items-start">
      <IconAlert
        aria-hidden="true"
        :class="[
          'size-7',
          {
            'text-blue-700 dark:text-blue-400':
              !alert.muted && alert.level === constants.alertLevel.INFO,
            'text-orange-500': !alert.muted && alert.level === constants.alertLevel.WARNING,
            'text-red-500': !alert.muted && alert.level === constants.alertLevel.ERROR,
            'text-gray-600': alert.muted,
          },
        ]"
      />
    </div>

    <div class="col-content mr-3 w-full p-0">
      <h3
        :class="[
          'mb-2 mr-10 font-sans text-base font-bold leading-[1.3]',
          {
            'mb-0 pb-0': alert.muted,
          },
        ]"
      >
        {{ alert.title }}
      </h3>

      <div v-show="!alert.muted">
        <div
          ref="messageRef"
          :class="['wrap-anywhere overflow-hidden text-sm', { 'line-clamp-3': !isMessageExpanded }]"
          v-dompurify-html="alert.message"
        />

        <KaButton
          v-if="isMessageTooLong"
          variant="link"
          class="mt-2 block p-0"
          @click="isMessageExpanded = !isMessageExpanded"
        >
          show {{ isMessageExpanded ? 'less' : 'more' }}
        </KaButton>

        <dl
          v-if="alert.firstSeen"
          class="wrap-anywhere my-4 grid grid-cols-[auto_1fr] gap-x-[1em] gap-y-[0.25em] text-sm"
        >
          <dt class="font-normal">First Seen</dt>
          <dd class="font-semibold text-gray-900 dark:text-gray-50">
            {{ shortDateTimeFormat(alert.firstSeen) }}
          </dd>
          <dt class="font-normal">Last Seen</dt>
          <dd class="font-semibold text-gray-900 dark:text-gray-50">
            {{ shortDateTimeFormat(alert.lastSeen) }}
          </dd>
        </dl>

        <template v-if="alert.button">
          <KaButton
            v-if="alert.button.dismiss"
            variant="primary"
            :class="cnMerge('action-button mt-4', alert.button.classes)"
            @click="$emit('buttonClick')"
          >
            {{ alert.button.text }}
          </KaButton>

          <KaLink
            v-else-if="alert.button.routeParams"
            :to="alert.button.routeParams"
            :class="cnMerge('mt-4', alert.button.classes)"
            @click="$emit('buttonClick')"
            variant="primary"
          >
            {{ alert.button.text }}

            <template #right>
              <IconChevronRight aria-hidden="true" class="size-4" />
            </template>
          </KaLink>

          <KaLink
            v-else-if="alert.button.href"
            :to="alert.button.href"
            :class="cnMerge('mt-4', alert.button.classes)"
            variant="primary"
          >
            {{ alert.button.text }}
          </KaLink>
        </template>
      </div>
    </div>

    <div class="flex w-12 flex-shrink-0 items-start justify-end">
      <KaButton type="button" @click="$emit('muteClick')" variant="link" size="small" class="p-0">
        {{ alert.muted ? 'unmute' : 'mute' }}
        <span class="sr-only">&nbsp;{{ alert.title }}</span>
      </KaButton>
    </div>
  </li>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';

import constants from '@/constants';
import { shortDateTimeFormat } from '@/filters';
import { KaButton, KaLink } from '@/kasten-components';
import type { AlertMessage } from '@/stores/main';
import { cnMerge } from '@/utils';
import IconChevronRight from '~icons/lucide/chevron-right';
import IconAlert from '~icons/lucide/triangle-alert';

const { alert } = defineProps<{
  alert: AlertMessage;
}>();

defineEmits<{
  buttonClick: [];
  muteClick: [];
}>();

const isMessageTooLong = ref(false);
const isMessageExpanded = ref(false);
const messageRef = ref<HTMLElement | null>(null);

const checkMessageLength = () => {
  if (messageRef.value) {
    isMessageTooLong.value = messageRef.value.scrollHeight > messageRef.value.clientHeight;
  }
};

watch(
  () => alert.message,
  async () => {
    await nextTick();
    checkMessageLength();
  },
  { immediate: true },
);
</script>
